/**
 * PlanSelector Component
 *
 * Main component for selecting a subscription plan.
 * Displays Pro and Pro+ plans with billing toggle and feature comparison.
 */

import { useState } from 'react';
import { PlansArray, DefaultPlan, DefaultBillingInterval } from '@enfomentis/shared/config';
import type { PlanType, BillingInterval } from '@enfomentis/shared/types';
import { PlanCard } from './PlanCard';
import { BillingToggle } from './BillingToggle';

interface PlanSelectorProps {
  /** Initial selected plan */
  initialPlan?: PlanType;
  /** Initial billing interval */
  initialInterval?: BillingInterval;
  /** Callback when user continues with selection */
  onContinue: (plan: PlanType, interval: BillingInterval) => void;
  /** Optional: Show as compact mode (smaller cards) */
  compact?: boolean;
}

export function PlanSelector({
  initialPlan = DefaultPlan,
  initialInterval = DefaultBillingInterval,
  onContinue,
  compact = false,
}: PlanSelectorProps) {
  const [selectedPlan, setSelectedPlan] = useState<PlanType>(initialPlan);
  const [billingInterval, setBillingInterval] =
    useState<BillingInterval>(initialInterval);

  const handleContinue = () => {
    onContinue(selectedPlan, billingInterval);
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-text-primary mb-2">
          Choose Your Plan
        </h2>
        <p className="text-text-secondary">
          Start your journey to better trading habits
        </p>
      </div>

      {/* Billing Toggle */}
      <div className="mb-8">
        <BillingToggle
          value={billingInterval}
          onChange={setBillingInterval}
          savingsPercent={17}
        />
      </div>

      {/* Plan Cards */}
      <div
        className={`grid gap-6 ${
          compact ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 lg:grid-cols-2'
        }`}
      >
        {PlansArray.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            billingInterval={billingInterval}
            isSelected={selectedPlan === plan.id}
            onSelect={() => setSelectedPlan(plan.id)}
            onContinue={selectedPlan === plan.id ? handleContinue : undefined}
          />
        ))}
      </div>

      {/* Footer Info */}
      <div className="mt-8 text-center">
        <p className="text-sm text-text-tertiary">
          All plans include a 14-day money-back guarantee. Cancel anytime.
        </p>
      </div>
    </div>
  );
}

/**
 * Standalone Plan Selection Page Component
 *
 * Full page layout for plan selection in the registration flow.
 */
interface PlanSelectionPageProps {
  onContinue: (plan: PlanType, interval: BillingInterval) => void;
}

export function PlanSelectionPage({ onContinue }: PlanSelectionPageProps) {
  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Logo/Brand Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-text-primary">EnfoMentis</h1>
          <p className="text-text-secondary mt-2">
            Where Discipline Meets Discovery
          </p>
        </div>

        {/* Plan Selector */}
        <PlanSelector onContinue={onContinue} />

        {/* Trust Indicators */}
        <div className="mt-12 flex flex-wrap justify-center gap-8 text-sm text-text-tertiary">
          <div className="flex items-center gap-2">
            <svg
              className="w-5 h-5 text-success"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            <span>Secure Payment</span>
          </div>
          <div className="flex items-center gap-2">
            <svg
              className="w-5 h-5 text-success"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            <span>Cancel Anytime</span>
          </div>
          <div className="flex items-center gap-2">
            <svg
              className="w-5 h-5 text-success"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </svg>
            <span>Money-Back Guarantee</span>
          </div>
        </div>
      </div>
    </div>
  );
}
